const PREFIX = 'climbwriter:v1:';
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const SNAPSHOT_INTERVAL = 10 * 60 * 1000;
const MAX_VERSIONS = 30;
const MAX_VERSION_BYTES = 1_500_000;

export const countWords = (text) => text.trim().match(/\S+/g)?.length ?? 0;
export const draftKey = (day) => `${PREFIX}draft:${day}`;
export const versionsKey = (day) => `${PREFIX}versions:${day}`;
export const goalKey = `${PREFIX}daily-goal`;

export function localDay(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function listDrafts(storage) {
  const drafts = {};
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key?.startsWith(`${PREFIX}draft:`)) continue;
    const day = key.slice(`${PREFIX}draft:`.length);
    if (DATE.test(day)) drafts[day] = storage.getItem(key) ?? '';
  }
  return drafts;
}

export function readVersions(storage, day) {
  if (!DATE.test(day)) return [];
  try {
    const value = JSON.parse(storage.getItem(versionsKey(day)) ?? '[]');
    return Array.isArray(value) ? value.filter(item =>
      item && typeof item.content === 'string' && Number.isFinite(item.savedAt)
    ) : [];
  } catch {
    return [];
  }
}

function writeSnapshot(storage, day, content, timestamp, force = false) {
  if (!content || !DATE.test(day)) return false;
  const versions = readVersions(storage, day);
  if (!force && versions[0] && timestamp - versions[0].savedAt < SNAPSHOT_INTERVAL) return false;
  if (versions[0]?.content === content) return false;
  versions.unshift({ content, savedAt: timestamp });
  versions.length = Math.min(versions.length, MAX_VERSIONS);
  while (versions.length > 1 && JSON.stringify(versions).length > MAX_VERSION_BYTES) versions.pop();
  storage.setItem(versionsKey(day), JSON.stringify(versions));
  return true;
}

export function saveDraft(storage, day, content, timestamp = Date.now(), forceSnapshot = false) {
  if (!DATE.test(day) || typeof content !== 'string') throw new Error('Invalid draft');
  const previous = storage.getItem(draftKey(day));
  if (previous === content) return { saved: false, snapshotSaved: false };
  // Save the newest text first. A snapshot failure must never undo the current draft.
  storage.setItem(draftKey(day), content);
  let snapshotSaved = false;
  try { snapshotSaved = writeSnapshot(storage, day, previous, timestamp, forceSnapshot); }
  catch { /* The current draft remains saved. */ }
  return { saved: true, snapshotSaved };
}

export function restoreVersion(storage, day, version, timestamp = Date.now()) {
  if (!version || typeof version.content !== 'string') throw new Error('Invalid recovery point');
  const current = storage.getItem(draftKey(day)) ?? '';
  if (current !== version.content) {
    // Preserve the version being replaced before restoring another one.
    if (current) writeSnapshot(storage, day, current, timestamp, true);
    storage.setItem(draftKey(day), version.content);
  }
  return version.content;
}

export function exportBackup(storage) {
  const drafts = listDrafts(storage);
  const versions = {};
  for (const day of Object.keys(drafts)) versions[day] = readVersions(storage, day);
  return {
    format: 'climbwriter-backup', version: 1, exportedAt: new Date().toISOString(),
    drafts, versions, dailyGoal: storage.getItem(goalKey),
  };
}

export function importBackup(storage, backup) {
  if (!backup || backup.format !== 'climbwriter-backup' || backup.version !== 1 ||
      !backup.drafts || typeof backup.drafts !== 'object' || Array.isArray(backup.drafts)) {
    throw new Error('This is not a ClimbWriter backup file.');
  }
  const entries = Object.entries(backup.drafts);
  if (entries.length > 2000 || entries.some(([day, content]) => !DATE.test(day) || typeof content !== 'string')) {
    throw new Error('The backup contains invalid drafts.');
  }
  let added = 0;
  let conflicts = 0;
  for (const [day, content] of entries) {
    const current = storage.getItem(draftKey(day));
    if (current === null) {
      storage.setItem(draftKey(day), content);
      added += 1;
      const versions = backup.versions?.[day];
      if (Array.isArray(versions) && versions.length <= MAX_VERSIONS && versions.every(item =>
        item && typeof item.content === 'string' && Number.isFinite(item.savedAt)
      )) {
        try { storage.setItem(versionsKey(day), JSON.stringify(versions)); } catch { /* Keep the restored draft. */ }
      }
    } else if (current !== content) {
      conflicts += 1; // Never overwrite writing already in this browser.
    }
  }
  const importedGoal = Number(backup.dailyGoal);
  if (storage.getItem(goalKey) === null && Number.isInteger(importedGoal) && importedGoal >= 50 && importedGoal <= 50000) {
    storage.setItem(goalKey, String(importedGoal));
  }
  return { added, conflicts };
}

export function writingStats(storage, today = localDay()) {
  const entries = Object.entries(listDrafts(storage))
    .map(([day, content]) => ({ day, words: countWords(content) }))
    .filter(item => item.words > 0 && item.day <= today)
    .sort((a, b) => a.day.localeCompare(b.day));
  const byDay = new Map(entries.map(item => [item.day, item.words]));
  const totalWords = entries.reduce((sum, item) => sum + item.words, 0);
  let streak = 0;
  const cursor = new Date(`${today}T12:00:00`);
  // An unfinished today does not break a streak that was active yesterday.
  if (!byDay.has(today)) cursor.setDate(cursor.getDate() - 1);
  while (byDay.has(localDay(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  const lastSeven = [];
  const dayCursor = new Date(`${today}T12:00:00`);
  dayCursor.setDate(dayCursor.getDate() - 6);
  for (let i = 0; i < 7; i += 1) {
    const day = localDay(dayCursor);
    lastSeven.push({ day, words: byDay.get(day) ?? 0 });
    dayCursor.setDate(dayCursor.getDate() + 1);
  }
  return { daysWritten: entries.length, totalWords, streak, lastSeven };
}
